import {
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type CSSProperties
} from "react";
import LitDialog, { Ref } from "./LitDialog";
import ModalHeader, { type ModalHeaderConfig } from "./ModalHeader";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { isOverlayEscapeKeyActive, useOverlayDismiss } from "@/hooks/useOverlayDismiss";
import SideSheetModal from "@/components/SideSheetModal";
import ModalContext from "@/hooks/useModalContext";
import {
  lockDocumentBodyScroll,
  resetDocumentBodyScrollLockForTests,
  unlockDocumentBodyScroll
} from "@/lib/documentBodyOverflow";

export function resetDialogBodyScrollLockForTests() {
  resetDocumentBodyScrollLockForTests();
}

export type { Ref };
export type { ModalHeaderConfig as DialogHeader };

export type Props = PropsWithChildren<{
  headline?: ReactNode;
  header?: ModalHeaderConfig;
  open?: boolean;
  onClosed?(): void;
  actions: ReactNode;
  className?: string;
  displayOverflow?: boolean;
  hasFormElements?: boolean;
  sheetZIndex?: number;
  fixedContent?: ReactNode;
  fixedContentCompact?: boolean;
  dismissOnOutsideClick?: boolean;
  disableClose?: boolean;
  closeButtonClassName?: string;
  /** Skips the Material open and close motion, including the container grow. */
  quick?: boolean;
}>;

export const dialogContainerStyle = {
  "--md-dialog-container-color": "var(--md-sys-color-surface-container-low)"
} as CSSProperties;

// Chrome's modal dialog has inset: 0. With height auto or fit-content that
// stretches the box to the window, then max-height shrinks it a moment later.
// top and bottom must not both be 0. Do not center with transform: a transform
// on the dialog makes position:fixed menus (the program list) use the dialog
// as their containing block, so viewport coordinates land outside the card.
const QUICK_DIALOG_WIDTH = "var(--m360-quick-dialog-width, min(560px, 90vw))";
const QUICK_DIALOG_MAX_WIDTH = "var(--m360-quick-dialog-max-width, var(--m360-quick-dialog-width, min(560px, 90vw)))";
const QUICK_DIALOG_MAX_HEIGHT = "var(--m360-quick-dialog-max-height, 90vh)";
const QUICK_DIALOG_TOP = `max(24px, calc((100vh - ${QUICK_DIALOG_MAX_HEIGHT}) / 2))`;
const QUICK_DIALOG_LEFT = `max(24px, calc((100vw - ${QUICK_DIALOG_WIDTH}) / 2))`;

const QUICK_DIALOG_CSS = `
dialog {
  width: ${QUICK_DIALOG_WIDTH} !important;
  max-width: ${QUICK_DIALOG_MAX_WIDTH} !important;
  height: fit-content !important;
  max-height: ${QUICK_DIALOG_MAX_HEIGHT} !important;
  top: ${QUICK_DIALOG_TOP} !important;
  left: ${QUICK_DIALOG_LEFT} !important;
  right: auto !important;
  bottom: auto !important;
  margin: 0 !important;
  transform: none !important;
  transition: none !important;
}
.scrim {
  opacity: 0 !important;
  transition: none !important;
}
`;

const quickDialogSheets = new WeakMap<Ref, CSSStyleSheet>();

function pinQuickDialogElement(dialog: HTMLDialogElement) {
  dialog.style.setProperty("width", QUICK_DIALOG_WIDTH, "important");
  dialog.style.setProperty("max-width", QUICK_DIALOG_MAX_WIDTH, "important");
  dialog.style.setProperty("height", "fit-content", "important");
  dialog.style.setProperty("max-height", QUICK_DIALOG_MAX_HEIGHT, "important");
  dialog.style.setProperty("top", QUICK_DIALOG_TOP, "important");
  dialog.style.setProperty("left", QUICK_DIALOG_LEFT, "important");
  dialog.style.setProperty("right", "auto", "important");
  dialog.style.setProperty("bottom", "auto", "important");
  dialog.style.setProperty("margin", "0", "important");
  dialog.style.setProperty("transform", "none", "important");
  dialog.style.setProperty("transition", "none", "important");
  const scrim = quickDialogScrim(dialog);
  if (scrim) {
    scrim.style.setProperty("opacity", "0", "important");
    scrim.style.setProperty("transition", "none", "important");
  }
  hideQuickDialogUntilCardSized(dialog);
}

function quickDialogScrim(dialog: HTMLDialogElement) {
  const root = dialog.getRootNode();
  const scrim = root instanceof ShadowRoot ? root.querySelector(".scrim") : null;
  return scrim instanceof HTMLElement ? scrim : null;
}

function revealQuickDialog(dialog: HTMLDialogElement) {
  if ("getAnimations" in dialog) dialog.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  const scrim = quickDialogScrim(dialog);
  if (scrim) scrim.style.setProperty("opacity", "0.32", "important");
  dialog.style.removeProperty("opacity");
}

function hideQuickDialogUntilCardSized(dialog: HTMLDialogElement) {
  if (dialog.dataset.quickReveal === "true") return;
  dialog.dataset.quickReveal = "true";
  const showModal = dialog.showModal;
  dialog.showModal = function showModalAfterCardSize() {
    dialog.style.setProperty("opacity", "0", "important");
    quickDialogScrim(dialog)?.style.setProperty("opacity", "0", "important");
    const result = showModal.call(dialog);
    const startedAt = performance.now();
    let lastHeight = -1;
    let stableFrames = 0;
    const reveal = () => {
      const height = dialog.getBoundingClientRect().height;
      const fillsWindow = height > window.innerHeight - 24;
      if (!fillsWindow && Math.abs(height - lastHeight) < 1) stableFrames += 1;
      else stableFrames = 0;
      lastHeight = height;
      const timedOut = performance.now() - startedAt > 700;
      if ((stableFrames >= 2 && !fillsWindow) || timedOut) {
        revealQuickDialog(dialog);
        return;
      }
      requestAnimationFrame(reveal);
    };
    requestAnimationFrame(reveal);
    return result;
  };
}

export function pinQuickDialog(host: Ref) {
  host.quick = true;
  const root = host.shadowRoot;
  const dialog = root?.querySelector("dialog");
  if (dialog instanceof HTMLDialogElement) pinQuickDialogElement(dialog);
  if (!root || quickDialogSheets.has(host)) return;

  const sheet = new CSSStyleSheet();
  sheet.replaceSync(QUICK_DIALOG_CSS);
  root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  quickDialogSheets.set(host, sheet);
  if (!dialog)
    void host.updateComplete.then(() => {
      const nextDialog = host.shadowRoot?.querySelector("dialog");
      if (nextDialog instanceof HTMLDialogElement) pinQuickDialogElement(nextDialog);
    });
}

const getCustomStyles = (dismissOnOutsideClick: boolean) => `
  ${!dismissOnOutsideClick ? `::backdrop { pointer-events: none; }` : ""}
  .scrim { 
    z-index: 50;
  }
`;

const Dialog = forwardRef<Ref, Props>(
  (
    {
      headline,
      header,
      actions,
      children,
      className,
      displayOverflow,
      open,
      onClosed,
      hasFormElements,
      sheetZIndex,
      fixedContent,
      fixedContentCompact = false,
      dismissOnOutsideClick = false,
      disableClose = false,
      closeButtonClassName = "!my-1",
      quick = false,
      ...props
    }: Props,
    ref
  ) => {
    const { isCompactLayout } = useIsCompactLayout();
    const litDialogRef = useRef<Ref | null>(null);
    const setLitDialogRef = useCallback(
      (node: Ref | null) => {
        litDialogRef.current = node;
        if (node && quick) pinQuickDialog(node);
      },
      [quick]
    );
    const closeLocked = header?.disableClose ?? disableClose;
    const allowOutsideDismiss = dismissOnOutsideClick && !closeLocked;
    const isCompactSheet = isCompactLayout && !!hasFormElements;
    const resolvedHeader: ModalHeaderConfig = {
      title: header?.title ?? headline ?? "",
      onBack: header?.onBack,
      action: header?.action,
      disableClose: closeLocked
    };

    const closeDialog = useCallback(() => {
      if (closeLocked) return;
      if (litDialogRef.current) {
        void litDialogRef.current.close();
        return;
      }
      onClosed?.();
    }, [closeLocked, onClosed]);

    useOverlayDismiss({
      open: !!open && !isCompactSheet,
      disableClose: closeLocked,
      dismiss: closeDialog
    });

    const handleCancel = (event: Event) => {
      if (closeLocked || isOverlayEscapeKeyActive()) {
        event.preventDefault();
        return;
      }
      if (!allowOutsideDismiss) event.preventDefault();
    };

    useImperativeHandle(ref, () => {
      if (isCompactLayout && hasFormElements) {
        return {
          close: async () => {
            if (onClosed) {
              onClosed();
            }
          },
          show: async () => {}
        } as Ref;
      }
      return litDialogRef.current as Ref;
    }, [isCompactLayout, hasFormElements, onClosed]);

    useEffect(() => {
      if (!open || typeof document === "undefined") {
        return;
      }

      lockDocumentBodyScroll();

      // Add this to ensure dialog content is scrollable on iOS
      const dialogElements = document.querySelectorAll("md-dialog");
      dialogElements.forEach((dialog) => {
        const dialogContainer = dialog.shadowRoot?.querySelector(".container") as HTMLElement;

        if (dialogContainer) {
          dialogContainer.style.position = "relative";
          (dialogContainer.style as any)["-webkit-overflow-scrolling"] = "touch";
        }
      });

      return () => {
        unlockDocumentBodyScroll();
      };
    }, [open]);

    // Temporary solution needed to override the overflow hidden on the dialog
    const overflowShownRef = useRef(false);
    useEffect(() => {
      if (typeof document === "undefined") return;
      if (displayOverflow) {
        overflowShownRef.current = true;
        const timer = setTimeout(showOverflow, 100);
        return () => clearTimeout(timer);
      }
      // A dialog that switches displayOverflow off gets its own content
      // scrolling back.
      if (overflowShownRef.current) {
        overflowShownRef.current = false;
        restoreOverflow();
      }
      // isCompactSheet: resizing out of the compact layout mounts a new md-dialog that needs the override.
    }, [displayOverflow, isCompactSheet]);

    function restoreOverflow() {
      const dialogContainer = litDialogRef.current?.shadowRoot?.querySelector(".container") as HTMLElement | null;
      const scrollerElement = dialogContainer?.querySelector(".scroller") as HTMLElement | null;
      if (dialogContainer) dialogContainer.style.overflow = "";
      if (scrollerElement) scrollerElement.style.overflow = "";
    }

    // Scoped to this dialog so sibling dialogs, open or closed, keep their own scrolling.
    function showOverflow() {
      const dialogContainer = litDialogRef.current?.shadowRoot?.querySelector(".container") as HTMLElement | null;
      const scrollerElement = dialogContainer?.querySelector(".scroller") as HTMLElement | null;
      if (dialogContainer) dialogContainer.style.overflow = "visible";
      if (scrollerElement) scrollerElement.style.overflow = "visible";
    }
    useEffect(() => {
      setTimeout(() => {
        if (typeof document !== "undefined") {
          const dialogElements = document.querySelectorAll("md-dialog");

          dialogElements.forEach((dialog) => {
            if (dialog.shadowRoot) {
              var sheet = new CSSStyleSheet();
              sheet.replaceSync(getCustomStyles(allowOutsideDismiss));
              dialog.shadowRoot.adoptedStyleSheets.push(sheet);
            }
          });
        }
      }, 50); // Short delay to ensure shadow DOM contents are rendered
    }, [allowOutsideDismiss]);

    return isCompactLayout && hasFormElements ? (
      <SideSheetModal
        isSidebarOpen={!!open}
        setSidebarOpen={(isOpen) => {
          if (!isOpen && onClosed) {
            onClosed();
          }
        }}
        header={resolvedHeader}
        actions={actions}
        fixedContent={fixedContentCompact ? fixedContent : undefined}
        disableFocusManagement
        dismissOnOutsideClick={allowOutsideDismiss}
        zIndex={sheetZIndex}
      >
        <ModalContext.Provider value={true}>
          <div className="w-full min-w-0" data-testid="dialog-compact-sheet-body">
            {children}
            {!fixedContentCompact && fixedContent && <div>{fixedContent}</div>}
          </div>
        </ModalContext.Provider>
      </SideSheetModal>
    ) : (
      <LitDialog
        quick={quick}
        onClosed={onClosed}
        onCancel={handleCancel}
        open={open}
        {...props}
        ref={setLitDialogRef}
        className={className}
        style={dialogContainerStyle}
        data-dismiss-on-outside-click={allowOutsideDismiss ? "true" : "false"}
      >
        <ModalHeader
          slot="headline"
          title={resolvedHeader.title}
          onBack={resolvedHeader.onBack}
          action={resolvedHeader.action}
          onClose={closeDialog}
          disableClose={resolvedHeader.disableClose}
          titleClassName="font-serif text-title-large-bold"
          backButtonClassName="!my-1 -ml-2.5"
          closeButtonClassName={closeButtonClassName}
        />
        <ModalContext.Provider value={true}>
          <div slot="content" className="!pb-6">
            {children}
          </div>
        </ModalContext.Provider>
        <div slot="actions">
          <div className="flex flex-col w-full gap-2">
            {fixedContent}
            <div className="flex flex-wrap justify-end gap-2">{actions}</div>
          </div>
        </div>
      </LitDialog>
    );
  }
);

Dialog.displayName = "Dialog";

export default Dialog;
